import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Radio } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";

const BADGE = { atp: "ATP", wta: "WTA", challenger: "Challenger", wta125: "WTA125", itf: "ITF", "grand-slam": "Grand Slam" };
const berlin = (ts, o = {}) => new Date(ts * 1000).toLocaleString("de-DE", { timeZone: "Europe/Berlin", ...o });
const day = (ts) => berlin(ts, { day: "2-digit", month: "2-digit" });
const box = "mt-2 max-h-56 overflow-y-auto rounded-lg border border-border text-sm";
const CACHE = { list: null };

function Step({ n, title, children }) {
  return <div className="rounded-xl border border-border p-3"><Label className="text-xs text-muted-foreground">{n}. {title}</Label>{children}</div>;
}

function TournamentStep({ value, onPick }) {
  const [list, setList] = useState(CACHE.list);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/feed/tournaments").then((r) => { CACHE.list = r.data; setList(r.data); }).catch((e) => { setList((l) => l || []); toast.error(errMsg(e)); }); }, []);
  const shown = useMemo(() => (list || []).filter((g) => g.name.toLowerCase().includes(q.toLowerCase())), [list, q]);
  return (
    <Step n={1} title="Tournament (running or starting within 7 days · BetsAPI)">
      <Input className="mt-1" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tournament…" data-testid="wizard-tournament-search" />
      <div className={box} data-testid="wizard-tournament-list">
        {list === null && <p className="flex items-center gap-2 p-3 text-muted-foreground" data-testid="wizard-loading"><Loader2 size={14} className="animate-spin" />Loading…</p>}
        {list && !shown.length && <p className="p-3 text-muted-foreground" data-testid="wizard-no-tournaments">No tournaments from BetsAPI – use manual mode below.</p>}
        {shown.map((g) => (
          <button key={g.key} onClick={() => onPick(g)} data-testid={`wizard-tournament-${g.key}`}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary ${value?.key === g.key ? "bg-secondary" : ""}`}>
            <Badge variant="outline" className="shrink-0">{BADGE[g.tour] || g.tour}</Badge>
            <span className="flex-1 truncate font-medium">{g.name}</span>
            {g.live > 0 && <span className="text-xs text-[var(--win)]">{g.live} live</span>}
            <span className="text-xs text-muted-foreground">{day(g.start)}–{day(g.end)} · {g.matches}</span>
          </button>
        ))}
      </div>
    </Step>
  );
}

function MatchStep({ tour, value, onPick }) {
  const [evs, setEvs] = useState(null);
  useEffect(() => { setEvs(null); api.get("/admin/feed/matches", { params: { league_ids: tour.league_ids.join(",") } }).then((r) => setEvs(r.data)).catch(() => setEvs([])); }, [tour]);
  return (
    <Step n={2} title={`Match · ${tour.name}`}>
      <div className={box} data-testid="wizard-match-list">
        {evs === null && <p className="p-3 text-muted-foreground">Loading…</p>}
        {evs && !evs.length && <p className="p-3 text-muted-foreground">No matches.</p>}
        {(evs || []).map((e) => (
          <button key={e.id} onClick={() => onPick(e)} data-testid={`wizard-match-${e.id}`}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary ${value === e.id ? "bg-secondary" : ""}`}>
            <span className="flex-1">{e.home} <span className="text-muted-foreground">vs</span> {e.away}</span>
            {e.round && <span className="text-xs text-muted-foreground">R{e.round}</span>}
            {e.live ? <span className="flex items-center gap-1 text-xs text-[var(--win)]"><Radio size={11} />{e.ss || "live"}</span>
              : <span className="text-xs text-muted-foreground">{berlin(e.time, { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>}
          </button>
        ))}
      </div>
    </Step>
  );
}

function MarketStep({ eid, f, onPick }) {
  const [m, setM] = useState(null);
  useEffect(() => { setM(null); api.get(`/admin/feed/markets/${eid}`).then((r) => setM(r.data)).catch(() => setM({ markets: [] })); }, [eid]);
  return (
    <Step n={3} title={`Market & pick${m?.bookmaker ? ` · ${m.bookmaker}` : ""}`}>
      {m === null && <p className="mt-2 text-sm text-muted-foreground">Loading odds…</p>}
      {m && !m.markets.length && <p className="mt-2 text-sm text-muted-foreground" data-testid="wizard-no-odds">No odds from BetsAPI – enter market, pick and odds manually below.</p>}
      <div className="mt-2 max-h-80 space-y-3 overflow-y-auto">
        {(m?.markets || []).map((mk) => (
          <div key={mk.key} data-testid={`wizard-market-${mk.key}`}>
            <p className="text-xs font-semibold">{mk.name}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {mk.outcomes.map((o, i) => {
                const on = f.market === mk.name && f.pick === o.label;
                return (
                  <button key={i} onClick={() => onPick(mk, o, m.bookmaker)} data-testid={`wizard-odd-${mk.key}-${i}`}
                    className={`rounded-md border px-2.5 py-1.5 text-xs transition-colors ${on ? "border-[var(--ball)] bg-[var(--ball)] text-black" : "border-border hover:border-[var(--ball)]"}`}>
                    {o.label} <b className="ml-1 font-num">{o.odds}</b>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Step>
  );
}

export function TipWizard({ f, setF }) {
  const [tour, setTour] = useState(null);
  const pickTour = (g) => {
    setTour(g);
    setF((x) => ({ ...x, tour: g.tip_tour, is_grand_slam: g.is_grand_slam, tournament_id: g.tournament_id || x.tournament_id, league: g.name, league_id: g.league_ids[0] }));
  };
  const pickMatch = (e) => setF((x) => ({ ...x, betsapi_event_id: e.id, home: e.home, away: e.away, start_time: new Date(e.time * 1000).toISOString(), league: e.league, league_id: e.league_id, round: e.round || x.round }));
  const pickOdd = (mk, o, book) => setF((x) => ({ ...x, market: mk.name, pick: o.label, odds: o.odds, bookmaker: book }));
  return (
    <div className="space-y-3" data-testid="tip-wizard">
      <TournamentStep value={tour} onPick={pickTour} />
      {tour && <MatchStep tour={tour} value={f.betsapi_event_id} onPick={pickMatch} />}
      {f.betsapi_event_id && <MarketStep eid={f.betsapi_event_id} f={f} onPick={pickOdd} />}
    </div>
  );
}
